import { useQuery } from '@tanstack/react-query';

import { UnauthQueryOptions } from '@/api';
import { filterMainBannerItems } from '@/app/donation/(home)/_utils/filterMainBannerItems';
import { DONATION_ROUTES } from '@/constants';
import { useWebviewTypeEntry } from '@/hooks/useWebviewTypeEntry';

export const useCategory = () => {
  const { entry } = useWebviewTypeEntry();

  //* 답례품 카테고리
  const { data: giftList, isPending: isGiftListPending } = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/home/gift-category/primary'](),
    select: (data) => {
      return data.context?.contents?.map((item) => ({
        id: `gift-${item.giftCategoryId}`,
        label: item.categoryName,
        href: DONATION_ROUTES.GIFTS.withSearchParams({
          searchParams: {
            categoryId: item.giftCategoryId,
          },
        }),
      }));
    },
  });

  //* 지역 리스트
  const { data: institutionList, isPending: isInstitutionListPending } =
    useQuery({
      ...UnauthQueryOptions[
        'get:/wello/v2/allows/home-love/main/institution-specialty'
      ](),
      select: (data) => {
        const sortedList =
          (data.context?.contents ?? [])
            .toSorted((a, b) => {
              return a.institutionName.localeCompare(b.institutionName);
            })
            .map((item) => ({
              id: `institution-${item.institutionId}`,
              label: item.institutionName,
              href: DONATION_ROUTES.HOMETOWNS_DETAIL.pathname({
                id: item.institutionId,
              }),
            }));

        return [
          {
            id: 'institution-all',
            label: '전체',
            href: DONATION_ROUTES.GIFTS.pathname,
          },
          ...sortedList,
        ];
      },
    });

  //* 이벤트 리스트
  const { data: eventList, isPending: isEventListPending } = useQuery({
    ...UnauthQueryOptions[
      'get:/wello/v2/allows/home/institution-event/ordered'
    ]({
      query: {
        excludeEndedYN: true,
        entry,
      },
    }),
    select: (data) => {
      const trimmed = filterMainBannerItems(
        data.context?.contents ?? [],
        entry,
      );

      return trimmed.map((item) => ({
        id: `event-${item?.eventId || item.title}`,
        label: item.title,
        href: item.href,
      }));
    },
  });

  const categoryData = [
    {
      id: 'category',
      label: '카테고리',
      children: [
        {
          id: 'gift',
          label: '답례품',
          children: giftList,
        },
        {
          id: 'institution',
          label: '지역',
          children: institutionList,
        },
        eventList && eventList.length > 0
          ? {
              id: 'event',
              label: '이벤트',
              children: eventList,
            }
          : null,
      ].filter((item): item is NonNullable<typeof item> => item !== null),
    },
  ];

  return {
    categoryData,
    isPending:
      isGiftListPending || isInstitutionListPending || isEventListPending,
  };
};
